import type {
    TExtractionStatus,
    TFileType,
    TExtractionType,
    TResponseProjectNameCheck,
    TResponsePropertyAddressCheck,
    TResponsePropertyNameCheck,
    TPropertyAddressCheckInfo,
    TAddressComponent,
    TCountryCode,
    TPropertySummary,
} from "@/apis/odin/apiODIN";

export type PropertySummary = TPropertySummary;

export type AddressComponent = TAddressComponent;

export type DealPropertyInfoWithId = {
    dealId: number;
    projectId?: number | null;
    dealName: string;
    description?: string | null;
    properties: Array<PropertyInfo>;
    modifiedAt?: string | null;
    modifiedBy?: string | null;
    createdBy: string;
    createdAt: string;
    coaId: number;
    nonOperatingAccountsInCoa: boolean;
};

export type DealInfo = {
    databaseId: number;
    dealName: string;
    description?: string | null;
    createdBy: string;
    modifiedAt?: string | null;
    modifiedBy?: string | null;
    dealId: number;
    projectId?: number | null;
    propertyId?: number | null;
    propertyName?: string | null;
    fullAddress?: string | null;
    createdAt: string;
    assetType?: AssetType | null;
    subAssetType?: SubAssetType | null;
    allowUserDeletion: boolean;
};

type ExtractionStatus = TExtractionStatus;

export type ExtractionType = TExtractionType;

// Keep in sync with the backend guard in project_router.py (update_project).
export const isCoaLockingExtractionType = (
    extractionType: ExtractionType,
): boolean => extractionType !== "rent_roll_extraction";

// A project's Chart of Accounts is locked while it has any CoA-locking (i.e.
// non-rent-roll) extraction on a live file. Soft-deleted files are ignored.
// Kept as a pure helper so it can be unit-tested; mirrors the backend guard in
// project_router.py (update_project).
type CoaLockingFile = {
    fileInfo: { deletedAt?: string | null };
    extractions: readonly { extractionType: ExtractionType }[];
};

export const hasCoaLockingExtractions = (
    files: readonly CoaLockingFile[],
): boolean =>
    files.some(file => {
        if (
            file.fileInfo.deletedAt !== undefined &&
            file.fileInfo.deletedAt !== null
        ) {
            return false;
        }
        return file.extractions.some(extraction =>
            isCoaLockingExtractionType(extraction.extractionType),
        );
    });

export type ExtractionInfo = {
    fileId: number;
    excelsiorFileId?: number | null;
    status?: ExtractionStatus | null;
    sheetName: string;
    sheetId: number;
    rowStart: number;
    rowEnd: number;
    colStart: number;
    colEnd: number;
    headerEnd: number;
    extractionId: number;
    dealId: number;
    projectId: number;
    createdAt: string;
    createdBy: string;
    modifiedAt?: string | null;
    modifiedBy?: string | null;
    completedAt?: string | null;
    deletedAt?: string | null;
    hasCompleted: boolean;
    extractionType: ExtractionType;
};

export type AddFileRow = {
    addFile: boolean;
    projectId: number;
    parentDealName: string;
    databaseId: number;
};

export type AddExtractionRow = {
    addExtraction: boolean;
    fileId: number;
    projectId: number;
    databaseId: number;
    parentFileName: string;
};

export type DealInfoWithParent = DealInfo & {
    isParent: boolean;
    numOfExtractions?: number;
    databaseCompanyName: string;
    numFiles: number;
    properties: PropertySummary[];
};

export type DealExtractionInfoWithParent = ExtractionInfo & {
    uploadedAt?: string;
    uploadedBy?: string;
    parentDealName: string;
    databaseId: number;
    databaseCompanyName: string;
    allowUserDeletion: boolean;
};
export type DealExtractionInfo =
    | DealInfoWithParent
    | DealExtractionInfoWithParent
    | AddFileRow;

export type FileInfoWithParent = FileInfo & {
    isParent: boolean;
    allowUserDeletion: boolean;
    status?: "with_proda" | null;
};
export type FileExtractionInfoWithParent = ExtractionInfo & {
    originalFilename?: string;
    uploadedAt?: string;
    uploadedBy?: string;
    uploadedByUserId?: number;
    fileType?: FileType | null;
    type?: ExtractionType | null;
    parentFileName: string;
    databaseId: number;
    extractionType: TExtractionType;
    allowUserDeletion: boolean;
};

export type FileExtractionInfo =
    | FileInfoWithParent
    | FileExtractionInfoWithParent
    | AddExtractionRow;

type PropertyInfo = {
    propertyName: string;
    fullAddress: string;
    zipCode?: string | null;
    locality?: string | null;
    country: string;
    assetType?: AssetType | null;
    component?: {
        [key: string]: {
            [key: string]: string;
        };
    } | null;
    googleName?: string | null;
    streetName?: string | null;
    streetNumber?: string | null;
    latitude?: number | null;
    longitude?: number | null;
    regionLevel1?: string | null;
    regionLevel2?: string | null;
    regionLevel1Short?: string | null;
    residentialUnits?: number | null;
    parkingUnits?: number | null;
    yearOfConstruction?: number | null;
    buildingType?: string | null;
    yearOfRenovation?: number | null;
    numOfFloors?: number | null;
    numOfBuildings?: number | null;
    propertyId: number;
};

export type FileInfo = {
    originalFilename: string;
    fileHash: string;
    bucketPath: string;
    dealId: number;
    projectId: number;
    databaseId: number;
    fileType?: FileType | null;
    uploadedBy: string;
    uploadedByUserId: number;
    virusDetails?: string | null;
    fileId: number;
    uploadedAt: string;
    deletedAt?: string | null;
};

type FileType = TFileType;

export type DocumentType =
    | "operating_statement"
    | "rent_roll"
    | "chart_of_accounts"
    | "other";

export const assetTypes = {
    ATOffice: "Office",
    ATRetail: "Retail",
    ATStorage: "Storage",
    ATLogistics: "Logistics",
    ATResidential: "Residential",
    ATHotel: "Hotel",
    ATLeisure: "Leisure",
    ATIndustrial: "Industrial",
    ATParking: "Parking",
    ATMixed: "Mixed",
    ATOther: "Other",
} as const;

export const subAssetTypes = {
    multifamily: "Multi-Family",
    manufactured_housing: "Manufactured Housing",
    // Mirrors Excelsior free text ODIN doesn't model; display-only — the
    // backend never writes "other" back, preserving the original text.
    other: "Other",
} as const;

export type AssetType = keyof typeof assetTypes;

export type SubAssetType = keyof typeof subAssetTypes;

export type DealNameCheck = TResponseProjectNameCheck;

export type PropertyAddressCheck = TResponsePropertyAddressCheck;

export type PropertyNameCheck = TResponsePropertyNameCheck;

export type MultipropBaseDealInfo = {
    dealName: string;
    description?: string | null;
    coaId: number;
};

export type ProjectInfo = MultipropBaseDealInfo & {
    projectId: number;
    databaseId: number;
};

export type CountryCode = TCountryCode;

export type MultipropBasePropertyInfo = {
    propertyName: string;
    fullAddress: string;
    zipCode?: string | null;
    locality?: string | null;
    country: CountryCode;
    assetType?: AssetType | null;
    subAssetType?: SubAssetType | null;
    component: {
        [key: string]: TAddressComponent;
    };
    googleName?: string | null;
    streetName?: string | null;
    streetNumber?: string | null;
    latitude?: number | null;
    longitude?: number | null;
    regionLevel1?: string | null;
    regionLevel2?: string | null;
    regionLevel1Short?: string | null;
    residentialUnits?: number | null;
    parkingUnits?: number | null;
    yearOfConstruction?: number | null;
    buildingType?: string | null;
    yearOfRenovation?: number | null;
    numOfFloors?: number | null;
    numOfBuildings?: number | null;
};

export type NewProperty = MultipropBasePropertyInfo & {
    projectId: number;
    databaseId: number;
    dealName: string;
    excelsiorPropertyId?: number | null;
};

export type MultipropBaseAddressComponent = TAddressComponent;

export type MultipropPropertyInfo = MultipropBasePropertyInfo & {
    propertyId: number;
    excelsiorPropertyId?: number | null;
};

export type MultipropDeal = MultipropBaseDealInfo & {
    dealId: number;
    projectId?: number | null;
    modifiedAt?: string | null;
    modifiedBy?: string | null;
    properties: Array<MultipropPropertyInfo>;
};

export type PropertyAddressCheckInfo = TPropertyAddressCheckInfo;

export type MultipropPropertyFormShape = {
    isAddressFormCollapsed: boolean;
    isDetailsFormCollapsed: boolean;
};

export type PropertyInfoForDelete = {
    // Property has completed rent rolls
    hasCompletedRr: boolean;
    // Property to delete
    propertyId: number;
};
